import { HttpFailedToFetchException } from "../model/httpException";

/**
 * Configuration constants for fetchWithRetry
 */
export const FETCH_RETRY_CONFIG = {
  /** Initial backoff delay in milliseconds for the first retry attempt */
  initialBackoffMs: 1750,
  /** Maximum number of retry attempts (total attempts = 1 initial + maxRetries) */
  maxRetries: 2,
} as const;

/**
 * Retry mechanism with exponential backoff
 * @param operation The async operation to retry
 * @param options Configuration options for the retry mechanism
 * @param options.maxRetries Maximum number of retry attempts
 * @param options.initialBackoffMs Initial backoff delay in milliseconds
 * @param options.operationName Optional name for the operation to improve logging
 * @param options.shouldRetry Optional predicate function to determine if an error should be retried. Returns false to skip retry.
 * @returns Promise<T> result of the operation
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialBackoffMs?: number;
    operationName?: string;
    shouldRetry?: (error: unknown) => boolean;
    onResultError?: (result: T) => unknown;
  } = {}
): Promise<T> {
  return retryWithBackoffInternal(operation, options, 0);
}

/**
 * Internal retry function that handles the retryCount parameter
 */
async function retryWithBackoffInternal<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialBackoffMs?: number;
    operationName?: string;
    shouldRetry?: (error: unknown) => boolean;
    onResultError?: (result: T) => unknown;
  },
  retryCount: number
): Promise<T> {
  const {
    maxRetries = 3,
    initialBackoffMs = 1000,
    // operationName,
    shouldRetry,
    onResultError,
  } = options;

  try {
    const result = await operation();
    if (retryCount < maxRetries && onResultError != undefined) {
      const err = onResultError(result);
      if (err !== undefined) {
        throw err;
      }
    }
    return result;
  } catch (error) {
    // Check if we should retry this error
    if (shouldRetry && !shouldRetry(error)) {
      throw error;
    }

    if (retryCount >= maxRetries) {
      throw error;
    }

    const backoffMs = initialBackoffMs * Math.pow(2, retryCount);

    await new Promise((resolve) => setTimeout(resolve, backoffMs));
    return retryWithBackoffInternal(operation, options, retryCount + 1);
  }
}

export async function fetchWithRetry(
  input: string | URL | globalThis.Request,
  init?: RequestInit
): Promise<Response> {
  return retryWithBackoff(
    async () => {
      return await fetch(input, init);
    },
    {
      initialBackoffMs: FETCH_RETRY_CONFIG.initialBackoffMs,
      maxRetries: FETCH_RETRY_CONFIG.maxRetries,
      shouldRetry() {
        return true;
      },
      onResultError(result) {
        if (
          result instanceof Response &&
          result.status >= 500 &&
          result.status <= 599
        ) {
          return new HttpFailedToFetchException();
        }
        return undefined;
      },
    }
  );
}
